import { ArrowLeft, FileText, Building2, ShieldCheck, AlertTriangle, Scale, Mail } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

const TermsAndConditions = () => {
  const navigate = useNavigate();
  const lastUpdated = "May 18, 2026";

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b">
        <div className="container max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold">Terms &amp; Conditions</h1>
            <p className="text-sm text-muted-foreground">Last updated: {lastUpdated}</p>
          </div>
        </div>
      </header>

      <main className="container max-w-4xl mx-auto px-4 py-8 space-y-8">
        <section className="space-y-4">
          <p className="text-lg text-muted-foreground">
            These Terms &amp; Conditions ("Terms") govern your access to and use of SORIN, a workplace safety intelligence platform operated by PT Sinar Inovasi Digital Indonesia ("SORIN", "we", "our", or "us"). By accessing or using the Service, you agree to be bound by these Terms.
          </p>
          <p className="text-muted-foreground">
            SORIN is a business-to-business (B2B) service. Subscriptions are purchased by companies for the benefit of their workers. If you are using SORIN as a worker, supervisor, or administrator, you are doing so under the subscription of your employer or contracting company.
          </p>
        </section>

        <Separator />

        <Card>
          <CardHeader className="flex flex-row items-center gap-3">
            <FileText className="h-6 w-6 text-primary" />
            <CardTitle>1. Acceptance of Terms</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-muted-foreground">
            <p>By creating an account, accessing, or using the Service, you confirm that you have read, understood, and agree to be bound by these Terms and our <Link to="/privacy-policy" className="text-primary underline">Privacy Policy</Link>.</p>
            <p>If you do not agree, you must not use the Service.</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center gap-3">
            <Building2 className="h-6 w-6 text-primary" />
            <CardTitle>2. Service Description &amp; B2B Subscriptions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-muted-foreground">
            <p>SORIN provides hazard reporting, behavioral safety analytics, AI-assisted intervention recommendations, micro-learning, and related workplace-safety tools.</p>
            <p>The Service is licensed to a subscribing company ("Customer") under a chosen subscription tier (Free, Professional, Enterprise, or Enterprise+). All workers, supervisors, and administrators added by the Customer use the Service under that subscription at no additional cost to the individual worker.</p>
            <p>Subscription tier features, limits, and pricing are listed on our website and may be updated from time to time. Upgrades are processed manually by our team after a sales conversation.</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center gap-3">
            <ShieldCheck className="h-6 w-6 text-primary" />
            <CardTitle>3. Acceptable Use</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-muted-foreground">
            <p>You agree to use the Service only for lawful workplace-safety purposes. You must not:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Submit knowingly false or malicious reports against other workers</li>
              <li>Attempt to gain unauthorized access to other tenants' data</li>
              <li>Reverse-engineer, decompile, or scrape the Service</li>
              <li>Use the Service to harass, intimidate, or punish workers</li>
              <li>Upload content that infringes third-party rights or contains malware</li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center gap-3">
            <ShieldCheck className="h-6 w-6 text-primary" />
            <CardTitle>4. Just Culture Commitment</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-muted-foreground">
            <p>SORIN operates under a Just Culture framework. Reports are scored by evidence strength rather than labeled as "true" or "false", and the Service must not be used as a punitive tool against individual workers. Customers agree to operate the Service consistent with this principle.</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center gap-3">
            <FileText className="h-6 w-6 text-primary" />
            <CardTitle>5. Data &amp; Privacy</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-muted-foreground">
            <p>Our collection, use, and protection of personal data is described in our <Link to="/privacy-policy" className="text-primary underline">Privacy Policy</Link>, which forms part of these Terms.</p>
            <p>The Customer is the data controller for worker data submitted through the Service; SORIN acts as data processor.</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>6. Intellectual Property</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-muted-foreground">
            <p>The Service, including all software, AI models, designs, trademarks, and documentation, is the exclusive property of SORIN and its licensors. No rights are granted other than the limited right to use the Service in accordance with these Terms.</p>
            <p>You retain ownership of content you submit, and you grant SORIN a worldwide license to host, process, and display that content as needed to operate the Service.</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center gap-3">
            <AlertTriangle className="h-6 w-6 text-primary" />
            <CardTitle>7. Disclaimers &amp; Limitation of Liability</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-muted-foreground">
            <p>The Service is provided "as is" and "as available". SORIN does not replace professional safety judgment, official inspections, or legal compliance obligations of the Customer.</p>
            <p>To the maximum extent permitted by law, SORIN shall not be liable for indirect, incidental, special, consequential, or punitive damages, or for any loss of profits, revenue, data, or workplace incidents arising from use of or inability to use the Service. Our aggregate liability shall not exceed the fees paid by the Customer in the twelve (12) months preceding the claim.</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>8. Term &amp; Termination</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-muted-foreground">
            <p>These Terms remain in effect for as long as you use the Service. We may suspend or terminate access for breach of these Terms, non-payment, or where required by law. Upon termination, the Customer may export its data within a reasonable period, after which data may be deleted.</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center gap-3">
            <Scale className="h-6 w-6 text-primary" />
            <CardTitle>9. Governing Law</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-muted-foreground">
            <p>These Terms are governed by the laws of the Republic of Indonesia. Any dispute arising under or in connection with these Terms shall be resolved in the courts of Jakarta, Indonesia, without prejudice to mandatory consumer protection laws of the user's residence.</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>10. Changes to These Terms</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">We may update these Terms from time to time. Material changes will be communicated through the Service or by email. Continued use of the Service after changes take effect constitutes acceptance of the revised Terms.</p>
          </CardContent>
        </Card>

        <Card className="border-primary/20 bg-primary/5">
          <CardHeader className="flex flex-row items-center gap-3">
            <Mail className="h-6 w-6 text-primary" />
            <CardTitle>Contact Us</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-muted-foreground">
            <p>Questions about these Terms? Reach out to us:</p>
            <p><strong className="text-foreground">Email:</strong> support@sorinsafety.com</p>
            <p><strong className="text-foreground">Company:</strong> PT Sinar Inovasi Digital Indonesia</p>
            <p><strong className="text-foreground">Website:</strong> https://sorinsafety.com</p>
          </CardContent>
        </Card>

        <div className="text-center text-sm text-muted-foreground py-8 space-y-1">
          <p>© {new Date().getFullYear()} SORIN Safety. All rights reserved.</p>
          <p>PT Sinar Inovasi Digital Indonesia</p>
        </div>
      </main>
    </div>
  );
};

export default TermsAndConditions;
